import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import CssBaseline from '@mui/material/CssBaseline';

// Components
import RedirectToHttps from './components/RedirectToHttps';
import { ThemeProvider as CustomThemeProvider } from './contexts/ThemeContext';
import { FontSizeProvider } from './contexts/FontSizeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PageTracking } from './hooks/usePageTracking';

// Pages
import Landing from './pages/Landing';
import ListDaily from './pages/ListDaily/ListDaily';
import ListPerformance from './pages/ListPerformance/ListPerformance';
import ListTechnicals from './pages/ListTechnicals/ListTechnicals';
import ListFundamentals from './pages/ListFundamentals/ListFundamentals';
import ListProsCons from './pages/ListProsCons/ListProsCons';
import ListAnalystViews from './pages/ListAnalystViews/ListAnalystViews';
import ListInsiders from './pages/ListInsiders/ListInsiders';
import TickerDaily from './pages/TickerDaily/TickerDaily';
import TickerTechnicals from './pages/TickerTechnicals/TickerTechnicals';
import TickerPerformance from './pages/TickerPerformance/TickerPerformance';
import TickerFundamentals from './pages/TickerFundamentals/TickerFundamentals';
import TickerProsCons from './pages/TickerProsCons/TickerProsCons';
import TickerAnalystViews from './pages/TickerAnalystViews/TickerAnalystViews';
import TickerInsiders from './pages/TickerInsiders/TickerInsiders';
import SignUp from './pages/SignUp/SignUp';
import Login from './pages/Login/Login';
import EmailVerification from './pages/EmailVerification/EmailVerification';
import ForgotPassword from './pages/ForgotPassword/ForgotPassword';
import ResetPassword from './pages/ResetPassword/ResetPassword';
import TermsOfUse from './pages/TermsOfUse/TermsOfUse';
import PrivacyPolicy from './pages/PrivacyPolicy/PrivacyPolicy';
import AboutUs from './pages/AboutUs/AboutUs';
import ManageAccount from './pages/ManageAccount/ManageAccount';
import ManageBilling from './pages/ManageBilling/ManageBilling';
import ContactUs from './pages/SendFeedback/SendFeedback';
import Pricing from './pages/Pricing/Pricing';
import GetStarted from './pages/GetStarted/GetStarted';
import OrderSummary from './pages/OrderSummary/OrderSummary';
import TechnicalIndicatorsTutorials from './pages/Tutorials/TechnicalIndicatorsTutorials';
import TechnicalIndicatorArticle from './pages/Tutorials/TechnicalIndicatorArticle';
import FundamentalIndicatorsTutorials from './pages/Tutorials/FundamentalIndicatorsTutorials';
import FundamentalIndicatorArticle from './pages/Tutorials/FundamentalIndicatorArticle';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (replaces cacheTime)
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Create theme
const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#D4AF37', // highlight-gold
    },
    secondary: {
      main: '#8B5CF6',
    },
    success: {
      main: '#2ECC71', // market-green
    },
    warning: {
      main: '#F1C40F', // market-gold
    },
    error: {
      main: '#E74C3C', // market-red
    },
    background: {
      default: '#121212', // background
      paper: '#1E1E1E', // background-card
    },
    text: {
      primary: '#E0E0E0', // text-primary
      secondary: '#A0A0A0', // text-secondary
    },
  },
  typography: {
    fontFamily: 'Manrope, system-ui, sans-serif',
    h4: {
      fontWeight: 700,
      color: '#D4AF37', // highlight-gold
    },
    h6: {
      fontWeight: 600,
      color: '#E0E0E0', // text-primary
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: '#1E1E1E', // background-card
          border: '1px solid #374151', // gray-700
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
  },
});

// Component to handle root route redirect for authenticated users
function RootRouteHandler() {
  const { isAuthenticated, loading } = useAuth();
  
  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-text-light-secondary dark:text-text-secondary">Loading...</p>
        </div>
      </div>
    );
  }
  
  // Redirect authenticated users to /daily
  if (isAuthenticated) {
    return <Navigate to="/daily" replace />;
  }
  
  // Show Landing page for non-authenticated users
  return <Landing />;
}

function AppContent() {
  const { loading } = useAuth();

  // Show loading state while checking authentication
  if (loading) {
    return (
      <CustomThemeProvider>
        <FontSizeProvider>
          <QueryClientProvider client={queryClient}>
            <ThemeProvider theme={theme}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <CssBaseline />
                {/* <RedirectToHttps /> */}
                <div className="flex items-center justify-center min-h-screen">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-text-light-secondary dark:text-text-secondary">Loading...</p>
                  </div>
                </div>
              </LocalizationProvider>
            </ThemeProvider>
          </QueryClientProvider>
        </FontSizeProvider>
      </CustomThemeProvider>
    );
  }

  // Show the same layout for both authenticated and non-authenticated users
  return (
    <CustomThemeProvider>
      <FontSizeProvider>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider theme={theme}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <CssBaseline />
              {/* <RedirectToHttps /> */}
              <Router>
                <PageTracking />
                          <Routes>
                          <Route path="/" element={<RootRouteHandler />} />
                          <Route path="/landing" element={<Landing />} />
                          <Route path="/daily" element={<ListDaily />} />
                          <Route path="/price" element={<Navigate to="/daily" replace />} />
                          <Route path="/performance" element={<ListPerformance />} />
                          <Route path="/technicals" element={<ListTechnicals />} />
                          <Route path="/fundamentals" element={<ListFundamentals />} />
                          <Route path="/proscons" element={<ListProsCons />} />
                          <Route path="/analysts" element={<ListAnalystViews />} />
                          <Route path="/insiders" element={<ListInsiders />} />
                          <Route path="/ticker/:ticker/daily" element={<TickerDaily />} />
                          <Route path="/ticker/:ticker/performance" element={<TickerPerformance />} />
                          <Route path="/ticker/:ticker/technicals" element={<TickerTechnicals />} />
                          <Route path="/ticker/:ticker/fundamentals" element={<TickerFundamentals />} />
                          <Route path="/ticker/:ticker/proscons" element={<TickerProsCons />} />
                          <Route path="/ticker/:ticker/analysts" element={<TickerAnalystViews />} />
                          <Route path="/ticker/:ticker/insiders" element={<TickerInsiders />} />
                          <Route path="/signup" element={<SignUp />} />
                          <Route path="/login" element={<Login />} />
                          <Route path="/verify-email" element={<EmailVerification />} />
                          <Route path="/forgot-password" element={<ForgotPassword />} />
                          <Route path="/reset-password" element={<ResetPassword />} />
                          <Route path="/terms-of-service" element={<TermsOfUse />} />
                          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                          <Route path="/about-us" element={<AboutUs />} />
                          <Route path="/manage-account" element={<ManageAccount />} />
                          <Route path="/manage-billing" element={<ManageBilling />} />
                          <Route path="/contact-us" element={<ContactUs />} />
                          <Route path="/pricing" element={<Pricing />} />
                          <Route path="/get-started" element={<GetStarted />} />
                          <Route path="/order-summary" element={<OrderSummary />} />
                          <Route path="/tutorials/technicals" element={<TechnicalIndicatorsTutorials />} />
                          <Route path="/tutorials/technicals/:slug" element={<TechnicalIndicatorArticle />} />
                          <Route path="/tutorials/fundamentals" element={<FundamentalIndicatorsTutorials />} />
                          <Route path="/tutorials/fundamentals/:slug" element={<FundamentalIndicatorArticle />} />
                          <Route path="*" element={<ListDaily />} />
                          </Routes>
              </Router>
            </LocalizationProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </FontSizeProvider>
    </CustomThemeProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
